import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import {
  DollarSign,
  Zap,
  Clock,
  FileCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import {
  getMenuEditorImports,
  getMenuEditorSummary,
  getMenuEditorAccounts,
} from '@/lib/api'

export function MenuEditorUsage() {
  const [page, setPage] = useState(1)
  const [accountFilter, setAccountFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const perPage = 50

  // Fetch accounts for filter dropdown
  const { data: accounts = [] } = useQuery({
    queryKey: ['menu-editor-accounts'],
    queryFn: getMenuEditorAccounts,
  })

  // Fetch imports with filters
  const { data: imports = [], isLoading: importsLoading, isError } = useQuery({
    queryKey: ['menu-editor-imports', page, accountFilter, statusFilter, dateFrom, dateTo],
    queryFn: () =>
      getMenuEditorImports({
        page,
        per_page: perPage,
        account_id: accountFilter !== 'all' ? accountFilter : undefined,
        success: statusFilter !== 'all' ? statusFilter === 'success' : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
  })

  // Fetch summary with same filters (except pagination)
  const { data: summary } = useQuery({
    queryKey: ['menu-editor-summary', accountFilter, dateFrom, dateTo],
    queryFn: () =>
      getMenuEditorSummary({
        account_id: accountFilter !== 'all' ? accountFilter : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      }),
  })

  const formatCurrency = (value: number | string) => `$${Number(value).toFixed(2)}`

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Menu Editor Usage</h1>
        <p className="text-muted-foreground">
          Track AI import costs and usage statistics
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary ? formatCurrency(summary.total_cost_usd) : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              Avg: {summary ? formatCurrency(summary.avg_cost_usd) : '-'} per import
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Imports</CardTitle>
            <FileCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.total_imports ?? '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              {summary ? `${summary.successful_imports} successful, ${summary.failed_imports} failed` : '-'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tokens Used</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary ? (summary.total_tokens_input + summary.total_tokens_output).toLocaleString() : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              {summary ? `${summary.total_tokens_input.toLocaleString()} in / ${summary.total_tokens_output.toLocaleString()} out` : '-'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Duration</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary ? `${Number(summary.avg_duration_sec).toFixed(1)}s` : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              Per import
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <Select
              value={accountFilter}
              onValueChange={(v) => {
                setAccountFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.account_id} value={account.account_id}>
                    {account.account_name} ({account.import_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v)
                setPage(1)
              }}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(1)
                }}
                className="w-[160px]"
                placeholder="From"
              />
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(1)
                }}
                className="w-[160px]"
                placeholder="To"
              />
            </div>

            {(accountFilter !== 'all' || statusFilter !== 'all' || dateFrom || dateTo) && (
              <Button
                variant="outline"
                onClick={() => {
                  setAccountFilter('all')
                  setStatusFilter('all')
                  setDateFrom('')
                  setDateTo('')
                  setPage(1)
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {importsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">Loading...</div>
            </div>
          ) : isError ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive m-4">
              Error loading data
            </div>
          ) : imports.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">No imports found</div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {imports.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {format(new Date(item.import_timestamp), 'MMM d, yyyy HH:mm')}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{item.account_name}</span>
                      </TableCell>
                      <TableCell>
                        {formatCurrency(item.cost_usd)}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            item.success
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }
                        >
                          {item.success ? 'Success' : 'Failed'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm text-muted-foreground">Page {page}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={imports.length < perPage}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
